import { useEffect } from 'react';

declare global {
  interface Window {
    fcSettings?: unknown;
    fcPreChatform?: {
      fcWidgetInit: (template: unknown) => void;
    };
    fcWidget?: {
      open?: () => void;
      show?: () => void;
    };
    __FRESHCHAT_WIDGET_INITIALIZED__?: boolean;
    __FRESHCHAT_OPEN_ON_READY__?: boolean;
  }
}

const PRECHAT_SCRIPT_SRC = 'https://snippets.freshchat.com/js/fc-pre-chat-form-v2.min.js';
const WIDGET_SCRIPT_SRC = 'https://wchat.freshchat.com/js/widget.js';

const PRE_CHAT_TEMPLATE = {
  // Form header color and Submit button color.
  mainbgColor: '#001e38',
  // Form Header Text and Submit button text color.
  maintxColor: '#fff',
  // Chat Form Title
  heading: 'BlueStone.com',
  // Chat form Welcome Message
  textBanner:
    "We can't wait to talk to you. But first, please take a couple of moments to tell us a bit about yourself.",
  // Submit Button Label.
  SubmitLabel: 'Start Chat',
  // Fields List - Maximum is 5
  // All the values are mandatory and the script will not work if not available.
  fields: {
    field2: {
      // Type for Name - Do not Change
      type: 'name',
      // Label for Field Name, can be in any language
      label: 'Name',
      // Default - Field ID for Name - Do Not Change
      fieldId: 'name',
      // Required "yes" or "no"
      required: 'yes',
      // Error text to be displayed
      error: 'Please Enter a valid name',
    },
    field3: {
      // Type for Email - Do Not Change
      type: 'email',
      // Label for Field Email, can be in any language
      label: 'Email',
      // Default - Field ID for Email - Do Not Change
      fieldId: 'email',
      // Required "yes" or "no"
      required: 'yes',
      // Error text to be displayed
      error: 'Please Enter a valid Email',
    },
    field4: {
      // Type for Phone - Do Not Change
      type: 'phone',
      // Label for Field Phone, can be in any language
      label: 'Phone',
      // Default - Field ID for Phone - Do Not Change
      fieldId: 'phone',
      // Required "yes" or "no"
      required: 'yes',
      // Error text to be displayed
      error: 'Please Enter a valid Phone Number',
    },
  },
};

function loadScriptOnce(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.defer = true;
    script.dataset.loaded = 'false';
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true },
    );
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
    document.body.appendChild(script);
  });
}

/**
 * Loads Freshchat widget on the client (safe for SSR).
 * - Waits 5s before loading (matches provided snippet)
 * - Loads pre-chat form script first
 * - Sets window.fcSettings before loading widget script
 * - Prevents duplicate initialization across route changes/hydration
 */
export function FreshchatWidget() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const tryOpen = () => {
      if (window.fcWidget?.open) {
        window.fcWidget.open();
        return true;
      }
      if (window.fcWidget?.show) {
        window.fcWidget.show();
        return true;
      }
      return false;
    };

    const handleOpenRequest = () => {
      if (!tryOpen()) {
        // Widget not ready yet; open once the script finishes loading.
        window.__FRESHCHAT_OPEN_ON_READY__ = true;
      }
    };

    window.addEventListener('freshchat:open', handleOpenRequest);

    if (window.__FRESHCHAT_WIDGET_INITIALIZED__) {
      // Widget already being managed; still keep the event listener alive.
      return () => {
        window.removeEventListener('freshchat:open', handleOpenRequest);
      };
    }

    window.__FRESHCHAT_WIDGET_INITIALIZED__ = true;

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          await loadScriptOnce(PRECHAT_SCRIPT_SRC, 'freshchat-prechat-script');

          window.fcSettings = {
            token: '398e54e6-bfff-4ccd-9925-175e4e020905',
            host: 'https://wchat.in.freshchat.com',
            config: {
              cssNames: {
                widget: 'custom_fc_frame',
                expanded: 'custom_fc_expanded',
              },
              disableNotifications: true,
              content: {
                headers: {
                  csat_question: 'Do you want to close the chat?',
                },
              },
              headerProperty: {
                appName: 'BlueStone',
                appLogo:
                  'https://kinclimg5.bluestone.com/f_webp/static/resources/themes/bluestone/images/ct/white_logo_1.v1.png',
                backgroundImage:
                  'https://kinclimg5.bluestone.com/f_webp/static/resources/themes/bluestone/images/new/success-gms-bg.jpg',
              },
            },
            onInit: function onInit() {
               
              console.log('widget init');
              window.fcPreChatform?.fcWidgetInit(PRE_CHAT_TEMPLATE);
            },
          };

          await loadScriptOnce(WIDGET_SCRIPT_SRC, 'freshchat-widget-script');

          window.dispatchEvent(new Event('freshchat:loaded'));

          if (window.__FRESHCHAT_OPEN_ON_READY__) {
            window.__FRESHCHAT_OPEN_ON_READY__ = false;
            tryOpen();
          }
        } catch (error) {
           
          console.error('Freshchat failed to load', error);
        }
      })();
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('freshchat:open', handleOpenRequest);
    };
  }, []);

  return null;
}

